/**
 * SearchFeatureModal — "PHACKTS Local Search" info modal.
 * Reads content from content.json searchFeature key.
 * Zero hardcoded prose. GPL-licensed shell.
 */

import { useEffect, useRef } from 'react';
import content from '../../content.json';
import type { SiteContent } from '../types';

const data = (content as unknown as SiteContent).searchFeature;

interface SearchFeatureModalProps {
  onClose: () => void;
}

const ICONS: Record<string, React.ReactNode> = {
  search: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
  ),
  shield: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
  ),
  layers: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
  ),
  globe: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  ),
  keyboard: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
  ),
  lock: (
    <svg className="w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
  ),
};

export default function SearchFeatureModal({ onClose }: SearchFeatureModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!data) return null;

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 md:p-8 bg-slate-950/90 backdrop-blur-md" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="search-feature-title">
      <div className="bg-slate-900 rounded-[2.5rem] w-full max-w-3xl max-h-[90vh] shadow-2xl overflow-y-auto custom-scrollbar border border-slate-800" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 px-8 py-6 border-b border-slate-800 bg-slate-900/95 backdrop-blur-sm flex items-center justify-between z-10">
          <div className="flex items-center gap-4 text-emerald-500">
            {ICONS.search}
            <h3 id="search-feature-title" className="text-xl font-bold uppercase tracking-tight text-white">{data.title}</h3>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} className="text-slate-500 hover:text-white p-2 rounded-full transition-all" aria-label="Close">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        {/* Body */}
        <div className="p-8 md:p-12 space-y-10">
          {/* Intro */}
          <p className="text-sm text-slate-400 leading-relaxed">{data.description}</p>

          {/* Feature points */}
          {data.points.map((point, i) => (
            <div key={i} className="flex items-start gap-6">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center shrink-0 border border-emerald-500/20" aria-hidden="true">
                {ICONS[point.icon] || ICONS.search}
              </div>
              <div>
                <h4 className="text-lg font-bold mb-2 text-white">{point.title}</h4>
                <p className="text-sm text-slate-400 leading-relaxed">{point.content}</p>
              </div>
            </div>
          ))}

          <div className="flex justify-center pt-4">
            <button type="button" onClick={onClose} className="px-10 py-4 bg-emerald-600 text-white rounded-2xl font-bold shadow-xl shadow-emerald-600/20 hover:bg-emerald-500 transition-all active:scale-95">
              {data.closeLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
